import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2, ArrowLeft } from "lucide-react";
import CampaignEditor from "./CampaignEditor";
import { formatApiError, mailingApi } from "./api";

// Short and personal beats long and polished for cold-outreach inbox placement.
// Filters key on phrases like "we are reaching out", "premium-grade", long bullet
// lists with adjective stacks, and generic "we supply X end to end" boilerplate.
// Keep the starter template human, brief, and specific to the recipient.
const blank = () => ({
  name: "Premium export note",
  subject: "{{name}}, Indian export supply from Vilora Impex",
  preheader: "A short note from Gujarat with catalogue, documents, and port-ready categories.",
  hero_headline: "Export-ready Indian goods, handled with port discipline.",
  hero_subheadline: "Rice, spice, dry goods, pulses, and produce from Gujarat with clean paperwork and buyer-specific packing.",
  hero_image_url: "",
  intro_paragraph: "Hello {{name}}, I'm Darshit from Vilora Impex. If {{company}} is reviewing Indian sourcing for {{country}}, I can share current availability, packing options, and shipment timelines for your next order.",
  bullets: [
    { title: "Certified supply", body: "FSSAI/APEDA-aligned paperwork, lab reports, and origin documents available by shipment." },
    { title: "Four desks", body: "Rice and grain, spices and masala, dry fruits and pulses, fresh produce." },
    { title: "Clear shipping", body: "Mundra-led routing, documented loading, and status updates until arrival." },
  ],
  cta_label: "View catalogue",
  cta_url: "https://www.viloraimpex.com",
});

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | template object

  const load = async () => {
    setLoading(true);
    try { setItems(await mailingApi.templates.list()); }
    catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      const t = await mailingApi.templates.create(blank());
      setItems((c) => [t, ...c]);
      setEditing(t);
    } catch { toast.error("Create failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await mailingApi.templates.remove(t.id);
      setItems((c) => c.filter((x) => x.id !== t.id));
      if (editing?.id === t.id) setEditing(null);
    } catch { toast.error("Delete failed"); }
  };

  if (editing) {
    return (
      <TemplateEditor
        template={editing}
        onClose={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="velora-micro text-[#3D1F00]/50">{items.length} templates</div>
        <button onClick={create} className="velora-btn-primary flex items-center gap-2">
          <Plus size={14} /> New template
        </button>
      </div>
      {loading ? (
        <div className="p-12 text-center velora-micro text-[#3D1F00]/40">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-12 border border-[#E0D4B6] bg-[#FFFFFF] text-center">
          <div className="velora-micro text-[#3D1F00]/50">No templates yet - create your first one.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div key={t.id} className="border border-[#E0D4B6] bg-[#FFFFFF] p-5 hover:border-[#E8943A]/60 transition cursor-pointer" onClick={() => setEditing(t)}>
              <div className="velora-serif text-lg text-[#3D1F00] mb-1">{t.name}</div>
              <div className="text-sm text-[#3D1F00]/60 mb-3 truncate">{t.subject}</div>
              <div className="flex items-center justify-between">
                <div className="velora-micro text-[#3D1F00]/30">
                  {t.bullets?.length || 0} bullets · CTA: {t.cta_label ? "yes" : "no"}
                </div>
                <button onClick={(e) => { e.stopPropagation(); remove(t); }} className="text-[#3D1F00]/30 hover:text-[#7B3F00]">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ template, onClose }) {
  const [draft, setDraft] = useState(template);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await mailingApi.templates.update(template.id, draft);
      toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#FAF3E0] py-2 z-10">
        <button onClick={onClose} className="velora-btn-ghost flex items-center gap-2"><ArrowLeft size={14}/> Back</button>
        <input
          value={draft.name || ""}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="velora-input flex-1 mx-4 max-w-md velora-serif !text-lg"
          placeholder="Template name"
        />
        <button onClick={save} disabled={saving} className="velora-btn-primary flex items-center gap-2">
          <Save size={14} /> {saving ? "Saving..." : "Save"}
        </button>
      </div>
      <CampaignEditor value={draft} onChange={setDraft} allowAttachments={false} />
    </div>
  );
}
